import accountService from "@/views/account/accountService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";

import type { Account, AccountNew, AccountQuery } from "./account";
import type { AxiosResponse } from "axios";
import type { RequestValidationError } from "@/helpers/requestValidationError";

const URL = "accounts";
const ENTITY_NAME = "Account";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_ODR_API_BASE_URL,
};
const api = apiService<Account>(apiHosts.INSIGHT, axiosParams);
const canDeleteApi = apiService<RequestValidationError>(apiHosts.INSIGHT, axiosParams);

const getAccounts = (params: AccountQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getAccountById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewAccount = async (ids: AccountIds) => {
  const defaultApp = {
    data: {
      ...accountService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<AccountNew, any>;

  return defaultApp;
};

type AccountIds = {
  id?: string | number;
};
const getAccountByIdOrNew = (ids: AccountIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewAccount(ids);
  } else {
    return getAccountById(ids.id, config) as Promise<AxiosResponse<AccountNew, any>>;
  }
};

const createAccount = (account: Account, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, account, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateAccount = (account: Account, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + account.id, account, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Account} account This cannot be a 'ref' to a account
 */
const saveAccount = (account: Account, config: PagingApiAndNotifierConfig = {}) => {
  if (account.id) {
    return updateAccount(account, config);
  } else {
    return createAccount(account, config);
  }
};

const deleteAccountById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteAccount = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.delete(URL + "/" + id + "?" + "validateOnly=" + true, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteAccountById,
  getAccounts,
  getAccountById,
  getNewAccount,
  getAccountByIdOrNew,
  createAccount,
  updateAccount,
  saveAccount,
  canDeleteAccount,
};
